import type { TaskTemplateCategory } from "./types";

export const MSP_OPERATIONS_TEMPLATES: TaskTemplateCategory = {
  id: "operations",
  name: "Operations",
  description: "Templates for operational tasks",
  templates: [
    {
      id: "monthly-report",
      name: "Generate Monthly Report",
      description: "Create monthly service report for client",
      title: "Monthly Report: [Client Name] - [Month Year]",
      description_text: `Generate comprehensive monthly service report:

**Report Sections:**
- [ ] Service summary
- [ ] Incident response
- [ ] System performance
- [ ] Recommendations
- [ ] Upcoming projects

**Data Sources:**
- RMM reports
- Ticket system data
- Performance metrics`,
      tags: ["reporting", "monthly", "client", "summary"],
      priority: "medium",
      status: "pending",
      category: "operations",
    },
    {
      id: "tech-consolidation-revenue-review",
      name: "Technology Consolidation & Revenue Opportunities Review",
      description:
        "Assess client's tech stack to consolidate tools and identify revenue opportunities",
      title: "Tech Consolidation & Revenue Opportunities: [Client Name]",
      description_text: `Conduct a structured review to consolidate technology and uncover revenue opportunities:

**Discovery & Objectives**
- [ ] Confirm client business goals and constraints (security, compliance, budget)
- [ ] Identify critical workflows and departments impacted

**Current State Inventory**
- [ ] Catalog all tools by category (communication, collaboration, security, infrastructure, data)
- [ ] Map owners, usage, cost, contract terms, renewal dates
- [ ] Identify overlapping capabilities and shadow IT

**Gap & Redundancy Analysis**
- [ ] Highlight duplicate tools and low-utilization licenses
- [ ] Evaluate integration friction and manual processes
- [ ] Assess risk areas (backup, identity, endpoint, email security)

**Consolidation & Standardization Plan**
- [ ] Propose standard toolset per category with rationale
- [ ] Define migration approach, risks, and rollback
- [ ] Outline identity strategy (SSO/MFA/SCIM) and device management

**Revenue Opportunities**
- [ ] Managed services upsell (monitoring, SOC, patching, backups, vCIO)
- [ ] Licensing optimization (consolidated SKUs, annual terms, right-sizing)
- [ ] Hardware refresh and lifecycle projects
- [ ] Implementation projects (migrations, integrations, automation)

**Stakeholders & Timeline**
- [ ] Identify decision-makers and champions
- [ ] Provide phased roadmap with milestones and KPIs

**Deliverables**
- Consolidation proposal with cost/benefit analysis
- Detailed bill of materials and pricing options
- Implementation timeline and success metrics`,
      tags: [
        "consolidation",
        "standardization",
        "cost-optimization",
        "licensing",
        "vendor",
        "upsell",
        "assessment",
      ],
      priority: "high",
      status: "pending",
      category: "operations",
    },
    {
      id: "monthly-invoicing-process",
      name: "Monthly Invoicing Process",
      description: "Complete monthly billing cycle for all clients",
      title: "Monthly Invoicing: [Month Year]",
      description_text: `Execute comprehensive monthly invoicing process:

**Pre-Billing Review:**
- [ ] Verify all time entries are logged and approved
- [ ] Check for any billable expenses or hardware purchases
- [ ] Review contract terms and billing schedules
- [ ] Confirm any special billing arrangements

**Invoice Generation:**
- [ ] Generate recurring service invoices
- [ ] Process project-based billing
- [ ] Add any one-time charges or overages
- [ ] Apply appropriate taxes and discounts

**Quality Assurance:**
- [ ] Review all invoices for accuracy
- [ ] Verify client information and billing addresses
- [ ] Check payment terms and due dates
- [ ] Ensure proper documentation and backup

**Distribution & Follow-up:**
- [ ] Send invoices via email and portal
- [ ] Update accounting system with invoice data
- [ ] Schedule payment follow-up tasks
- [ ] Track payment status and aging`,
      tags: ["invoicing", "billing", "revenue", "monthly", "accounting"],
      priority: "high",
      status: "pending",
      category: "operations",
    },
    {
      id: "accounts-receivable-review",
      name: "Accounts Receivable Review",
      description: "Review and follow up on outstanding client payments",
      title: "AR Review: [Client Name] - [Amount]",
      description_text: `Conduct accounts receivable review and follow-up:

**Payment Status Review:**
- [ ] Check payment history and aging report
- [ ] Identify overdue invoices and amounts
- [ ] Review payment terms and grace periods
- [ ] Assess client payment patterns

**Follow-up Actions:**
- [ ] Send payment reminders (email/phone)
- [ ] Schedule payment discussions with client
- [ ] Review any disputed charges or issues
- [ ] Escalate to management if needed

**Resolution Planning:**
- [ ] Propose payment plans for large balances
- [ ] Consider service suspension for non-payment
- [ ] Update client credit terms if necessary
- [ ] Document all communication and agreements

**Reporting:**
- [ ] Update AR aging report
- [ ] Report on collection efforts and results
- [ ] Identify trends and improvement opportunities`,
      tags: ["accounts-receivable", "collections", "payment-follow-up", "cash-flow"],
      priority: "high",
      status: "pending",
      category: "operations",
    },
    {
      id: "contract-renewal-review",
      name: "Contract Renewal Review",
      description: "Review and prepare for upcoming contract renewals",
      title: "Contract Renewal: [Client Name] - [Contract Type]",
      description_text: `Prepare for contract renewal negotiations:

**Contract Analysis:**
- [ ] Review current contract terms and pricing
- [ ] Assess service delivery performance
- [ ] Identify any scope changes or additions
- [ ] Calculate renewal pricing and options

**Client Relationship Assessment:**
- [ ] Evaluate client satisfaction and feedback
- [ ] Review service level agreements and metrics
- [ ] Identify any issues or concerns
- [ ] Assess client growth and changing needs

**Renewal Strategy:**
- [ ] Prepare renewal proposal with pricing
- [ ] Identify upsell and cross-sell opportunities
- [ ] Schedule renewal discussion meeting
- [ ] Prepare negotiation strategy and fallbacks

**Documentation & Execution:**
- [ ] Draft new contract terms
- [ ] Coordinate legal review if needed
- [ ] Schedule client signature and execution
- [ ] Update systems with new contract details`,
      tags: ["contract-renewal", "pricing", "client-relations", "negotiation"],
      priority: "high",
      status: "pending",
      category: "operations",
    },
    {
      id: "revenue-forecasting",
      name: "Revenue Forecasting & Analysis",
      description: "Analyze revenue trends and forecast future performance",
      title: "Revenue Forecast: [Quarter/Year]",
      description_text: `Conduct comprehensive revenue analysis and forecasting:

**Historical Analysis:**
- [ ] Review revenue trends over past 12 months
- [ ] Analyze client retention and churn rates
- [ ] Identify seasonal patterns and variations
- [ ] Calculate average deal size and sales cycle

**Pipeline Analysis:**
- [ ] Review current sales pipeline and opportunities
- [ ] Assess probability of closing each deal
- [ ] Identify potential risks and blockers
- [ ] Calculate weighted pipeline value

**Forecasting:**
- [ ] Project revenue for next quarter/year
- [ ] Account for known renewals and expansions
- [ ] Factor in market conditions and trends
- [ ] Create best-case, likely, and worst-case scenarios

**Action Planning:**
- [ ] Identify gaps between forecast and targets
- [ ] Develop strategies to close revenue gaps
- [ ] Plan resource allocation and priorities
- [ ] Set up tracking and monitoring systems`,
      tags: ["forecasting", "revenue-analysis", "pipeline", "planning"],
      priority: "medium",
      status: "pending",
      category: "operations",
    },
    {
      id: "vendor-management-review",
      name: "Vendor Management & Cost Optimization",
      description: "Review vendor relationships and optimize costs",
      title: "Vendor Review: [Vendor/Service Category]",
      description_text: `Conduct vendor management and cost optimization review:

**Vendor Performance Analysis:**
- [ ] Review service level agreements and performance
- [ ] Assess vendor responsiveness and support quality
- [ ] Evaluate pricing competitiveness and value
- [ ] Check contract terms and renewal dates

**Cost Optimization Opportunities:**
- [ ] Identify potential cost savings and efficiencies
- [ ] Compare pricing with alternative vendors
- [ ] Negotiate better terms and volume discounts
- [ ] Consolidate services where possible

**Relationship Management:**
- [ ] Schedule quarterly business reviews
- [ ] Address any service issues or concerns
- [ ] Explore partnership and growth opportunities
- [ ] Update vendor scorecards and evaluations

**Contract Management:**
- [ ] Review and update contract terms
- [ ] Ensure compliance with service agreements
- [ ] Plan for contract renewals and negotiations
- [ ] Document all vendor communications and decisions`,
      tags: ["vendor-management", "cost-optimization", "contracts", "negotiation"],
      priority: "medium",
      status: "pending",
      category: "operations",
    },
  ],
};
